// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson

/**
 * Every piece of jargon this site uses, in plain English. The reader is assumed
 * to know nothing about the Australian Public Service — which is the normal
 * case, since most visitors are not public servants.
 */
export interface GlossaryEntry {
  term: string;
  definition: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  aps: {
    term: 'APS (Australian Public Service)',
    definition:
      'The federal government\'s permanent civilian workforce — the people employed under the Public Service Act 1999 in departments and agencies. It does NOT include the Australian Defence Force, parliamentarians and their staff, contractors, or state and local government employees.',
  },
  apsed: {
    term: 'APSED',
    definition:
      'The APS Employment Database, run by the Australian Public Service Commission. Agencies report their staffing to it, and the Commission publishes summary tables twice a year — a snapshot at 30 June and one at 31 December.',
  },
  headcount: {
    term: 'Headcount',
    definition:
      'A count of people, not of full-time-equivalent positions. Someone working two days a week counts as one person here, the same as someone working five. That is why headcount is always larger than an FTE figure for the same workforce.',
  },
  classification: {
    term: 'Classification',
    definition:
      'The APS rank structure, from most junior to most senior: Trainee and Graduate, then APS 1 through APS 6, then Executive Level 1 and 2, then the Senior Executive Service (SES) bands 1, 2 and 3. Pay and responsibility rise with each step.',
  },
  el: {
    term: 'Executive Level (EL 1, EL 2)',
    definition:
      'The two middle-management classifications sitting above APS 6 and below the Senior Executive Service. EL 1 is roughly a senior specialist or team leader; EL 2 typically runs a section or a branch.',
  },
  ses: {
    term: 'SES (Senior Executive Service)',
    definition:
      'The most senior classifications in the APS — bands 1, 2 and 3, in rising order. SES employees lead branches, divisions and groups. Roughly one in every 55 APS employees is in the SES.',
  },
  baseClassification: {
    term: 'Base classification',
    definition:
      'The rank someone is substantively employed at, as opposed to a higher rank they may be temporarily acting in. This site uses base classification throughout, because it is the one reported consistently across all twenty years.',
  },
  ongoing: {
    term: 'Ongoing / non-ongoing',
    definition:
      'The APS terms for permanent and non-permanent employment. Ongoing employees hold continuing positions; non-ongoing employees are on fixed terms, casual, or irregular arrangements.',
  },
  sa4: {
    term: 'SA4 (Statistical Area Level 4)',
    definition:
      'The Australian Bureau of Statistics\' regional building block — 88 areas covering the whole country, each typically holding 100,000 to 500,000 people. Big cities are split into several SA4s (Melbourne alone has nine); whole rural regions form one.',
  },
  agency: {
    term: 'Agency',
    definition:
      'An employing body in the APS. This site distinguishes the 16 departments of state from the 87 other agencies, authorities, commissions and tribunals grouped under a portfolio. A department\'s figures count only that department, not the other bodies in its portfolio.',
  },
  portfolio: {
    term: 'Portfolio',
    definition:
      'A minister\'s area of responsibility — a department plus the other agencies that report through it. Home Affairs, for example, is a department and also a portfolio containing several separate agencies.',
  },
  mog: {
    term: 'Machinery of Government (MoG) change',
    definition:
      'A government reorganisation that merges, splits, renames or abolishes departments and moves functions between them. MoG changes are frequent, which is why comparing one agency\'s headcount across many years is unreliable — the agency may not mean the same thing at both ends.',
  },
  suppressed: {
    term: 'Confidentialised',
    definition:
      'The APSC replaces a figure with a full stop when the number of people involved is too small to publish without risking someone being identified. A confidentialised cell is NOT zero — real people sit behind it. This site draws those cells as hatched, never as an empty or zero value.',
  },
  genderX: {
    term: 'Gender X',
    definition:
      'Employees who report a gender other than man or woman. The APSC does not publish them as a separate line because the counts are small, but it does include them in every total. That is why the men and women figures on this site add up to slightly less than the total.',
  },
  exitRate: {
    term: 'Exit rate',
    definition:
      'The share of an agency\'s ongoing employees who left THAT AGENCY during the year — including those who simply moved to another APS agency. It is therefore always higher than the separation rate.',
  },
  separationRate: {
    term: 'Separation rate',
    definition:
      'The share of ongoing employees who left the APS altogether during the year — resignation, retirement, retrenchment or termination. Someone who transfers between agencies is not a separation.',
  },
  promotion: {
    term: 'Promotion',
    definition:
      'A move to a higher classification. The APS records whether it happened inside the employee\'s own agency or involved moving to a different one, which is what makes agency-to-agency talent flows measurable.',
  },
  cald: {
    term: 'CALD',
    definition:
      'Culturally and linguistically diverse — in APSED, employees who report being born overseas in a country where English is not the main language spoken.',
  },
  medianLos: {
    term: 'Median length of service',
    definition:
      'The number of years the middle employee has worked in the APS. Half the agency\'s staff have been in the service longer than this, half less.',
  },
  jobFamily: {
    term: 'Job family',
    definition:
      'A grouping of similar roles — Service Delivery, Policy, ICT, Regulation, and so on. Some agencies report a share of staff as "no data", which this site shows as unclassified rather than hiding or reassigning it.',
  },
};

export function glossaryTerm(key: string): GlossaryEntry | undefined {
  return GLOSSARY[key];
}
